import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { i18n } from "@/locales";
import { authApi } from '@/api/authApi';
import { AuthContext } from './AuthContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function AuthProviderContent({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data: user, isLoading, refetch } = useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: async () => {
      try {
        return await authApi.getProfile();
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    retry: false,
  });

  const isAuthenticated = !!user && !isLoading;

  // Set i18n language from user preferences when user loads
  useEffect(() => {
    if (user?.language && i18n.language !== user.language) {
      i18n.changeLanguage(user.language);
    }
  }, [user?.language]);

  const login = () => {
    // Trigger redirect to Google OAuth
    // Note: This never returns - page will redirect to /auth/google
    authApi.initiateGoogleLogin();
  };

  const logout = async () => {
    try {
      await authApi.logout();
      queryClient.setQueryData(['auth', 'profile'], null);
      // Let the calling component decide where to navigate based on context
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const refetchUser = async () => {
    await refetch();
  };

  const setUser = (newUser: typeof user) => {
    queryClient.setQueryData(['auth', 'profile'], newUser);
  };

  return (
    <AuthContext.Provider
      value={{
        user: user || null,
        isAuthenticated,
        isLoading,
        login,
        logout,
        refetchUser,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProviderContent>{children}</AuthProviderContent>
    </QueryClientProvider>
  );
}
